import { Group, KonvaEventObject, Layer } from "konva/lib/Core";
import { Factory } from "konva/lib/Factory";
import { getBooleanValidator } from "konva/lib/Validators";
import { Transformer } from "konva/lib/shapes/Transformer";
import { Rect } from "konva/lib/shapes/Rect";
import { Text } from "konva/lib/shapes/Text";
import { IRect } from "konva/types/types";
import { IPosition, WhiteboardShape, scaleSize, ISizeableState, nodeSize, calculatePosition } from "../shapes";
import { fireStateEvent } from "../events";
import { relativePointerPosition } from "../util";
import { SHAPE_SELECTED, SHAPE_UNSELECTED } from "@gtmhub/whiteboards/rendering/behaviors/shape-context-menu";
import { IWhiteboardRendererContext } from "@gtmhub/whiteboards";
import Konva from "konva";

export interface ISelectionOptions {
  minSize?: number;
  resizable?: boolean;
  rotatable?: boolean;
}

export interface ISelectable {
  selected(): boolean;
  selected(selected: boolean): this;
  selectionOptions: ISelectionOptions;
}

export function addSelectedGetterSetter<S extends Group, C extends { new (...args: unknown[]): S }>(ctor: C, options: ISelectionOptions = {}): void {
  Factory.addGetterSetter(ctor, "selected", false, getBooleanValidator());
  ctor.prototype.selectionOptions = options;
}

const containsRect = (container: IRect, rect: IRect) => Konva.Util.haveIntersection(container, rect);

export interface IShapeSelectionRenderer {
  selectedShapes(): WhiteboardShape[];
  selectedShapes(shapes: WhiteboardShape[]): void;
  selectedShapes(shapes?: WhiteboardShape[]): void | WhiteboardShape[];
  adjustLockIfLocked(shape: WhiteboardShape): void;
  lockShape(shape: WhiteboardShape, config: { color: string; userFirstName: string }): void;
  unlockShape(...shapes: WhiteboardShape[]): void;
  isShapeLocked(shapeId: string): boolean;
}

export const configureShapeSelection = <Renderer extends IShapeSelectionRenderer>(renderer: Renderer, layer: Layer, context: IWhiteboardRendererContext): void => {
  const stage = layer.getStage();
  let isMultiSelecting: boolean;
  let initialPointerPos: IPosition;
  let clickedShape: WhiteboardShape;
  let isTransforming: boolean;
  const selection = { shapes: [] as WhiteboardShape[] };
  const selectedSingleShape = () => selection.shapes.length === 1 && selection.shapes[0];
  let mouseUpOnStageIsFired = false;

  //
  // single selection
  //

  // we transform a transparent background node, because shapes are Group objects without size
  const singleSelectionBackgroundRect = new Rect({ listening: false, visible: false });
  layer.add(singleSelectionBackgroundRect);

  const selectionTr = new Transformer({
    padding: 10,
    borderStroke: "#96c6ff",
    anchorCornerRadius: 50,
    anchorSize: 7,
    anchorStroke: "#96c6ff",
    rotateAnchorOffset: 80,
    rotateEnabled: false,
    keepRatio: false,
    ignoreStroke: true,
    visible: false,
    enabledAnchors: ["top-left", "top-right", "bottom-left", "bottom-right"],
    boundBoxFunc: (oldBoundBox, newBoundBox) => {
      // keep min width/height
      const selectionOptions = selectedSingleShape().selectionOptions;
      if (selectionOptions.minSize && (newBoundBox.width < selectionOptions.minSize || newBoundBox.height < selectionOptions.minSize)) {
        return oldBoundBox;
      }

      return newBoundBox;
    },
  });

  selectionTr.on("transformstart", (evt) => {
    selectedSingleShape().fire("transformstart", evt);
  });

  selectionTr.on("transform", (evt) => {
    const isEllipse = selectedSingleShape().state.type === "ellipse";
    const isLine = selectedSingleShape().state.type === "rhombus" || selectedSingleShape().state.type === "parallelogram";

    const pos = calculatePosition({ isEllipse: isEllipse, position: singleSelectionBackgroundRect.position(), size: singleSelectionBackgroundRect.size() });

    selectedSingleShape()
      .setAttrs({
        scaleX: singleSelectionBackgroundRect.scaleX(),
        scaleY: singleSelectionBackgroundRect.scaleY(),
        x: isLine ? selectedSingleShape().x() : pos.x,
        y: isLine ? selectedSingleShape().y() : pos.y,
        rotation: singleSelectionBackgroundRect.rotation(),
      })
      .fire("transform", evt);

    if (isEllipse || isLine) {
      fireStateChange(evt, { notify: false });
    }
  });

  selectionTr.on("transformend", (evt) => {
    selectedSingleShape().fire("transformend", evt);
    fireStateChange(evt, { notify: true });
  });

  layer.add(selectionTr);

  const shapeSize = (shape: WhiteboardShape) => (shape.state as unknown as ISizeableState).size || nodeSize(shape, (n) => !n.hasName("voting"));

  const shapeEvents = ["dragmove", "resize", "xChange", "yChange"];
  const lineAndEllipseEvents = ["dragmove", "resize"];

  const shapeEventsThatAffectPosition = (shape: WhiteboardShape) => {
    const isEllipseOrLine = shape.state.type === "rhombus" || shape.state.type === "parallelogram" || shape.state.type === "ellipse";

    if (isEllipseOrLine) {
      return lineAndEllipseEvents.map((x) => `${x}.single-selection`).join(" ");
    }
    return shapeEvents.map((x) => `${x}.single-selection`).join(" ");
  };

  const guaranteedHighestIndex = 1000;

  const attachSingleSelectionBehavior = (shape: WhiteboardShape) => {
    shape.zIndex(guaranteedHighestIndex);

    const rotation = shape.rotation() !== shape.state.rotationAngle ? shape.rotation() : shape.state.rotationAngle;

    singleSelectionBackgroundRect.setAttrs({
      visible: true,
      ...shape.getPosition(),
      scaleX: shape.scaleX(),
      scaleY: shape.scaleY(),
      ...shapeSize(shape),
      rotation,
    });

    shape.selected(true);
    shape.on(shapeEventsThatAffectPosition(shape), () => {
      singleSelectionBackgroundRect.position(shape.getPosition()).size(shapeSize(shape)).scale(shape.scale());
    });

    layer.batchDraw();
  };

  const resetSingleSelectionBehavior = (shape: WhiteboardShape) => {
    shape.zIndex(shape.state.zIndex);

    shape.selected(false);
    shape.off(shapeEventsThatAffectPosition(shape));

    singleSelectionBackgroundRect.hide();
    selectionTr.hide();
  };

  const fireStateChange = (evt?: KonvaEventObject<unknown>, options?: { notify?: boolean }): void => {
    const position = singleSelectionBackgroundRect.position();
    const size = scaleSize(singleSelectionBackgroundRect.scale(), singleSelectionBackgroundRect.size());
    const rotationAngle = singleSelectionBackgroundRect.rotation();

    fireStateEvent("stateChange", evt, selectedSingleShape(), { state: selectedSingleShape().state, changes: { position, size, rotationAngle }, notify: options.notify });
  };

  const setSelectedShapes = (shapes: WhiteboardShape[], evt?: KonvaEventObject<MouseEvent>) => {
    const isSingleSelection = shapes.length === 1;
    const isSelectingAlreadySelectedSingleShape = isSingleSelection && selection.shapes.some((shape) => shape.id() === shapes[0].id());
    if (isSelectingAlreadySelectedSingleShape) {
      return;
    }

    const prevSelectedSingleShape = selectedSingleShape();
    if (prevSelectedSingleShape) {
      const shouldResetPrevSelectedShape = !isSingleSelection || prevSelectedSingleShape.id() !== shapes[0].id();
      if (shouldResetPrevSelectedShape) {
        resetSingleSelectionBehavior(prevSelectedSingleShape);
      }
    }

    selection.shapes = shapes;

    if (isSingleSelection) {
      attachSingleSelectionBehavior(shapes[0]);
    }

    let selectedShapes = [];

    if (shapes.length > 1) {
      selectedShapes = shapes;
    } else if (shapes.length === 1) {
      selectedShapes = [singleSelectionBackgroundRect];
    }

    selectionTr
      .nodes(selectedShapes)
      .resizeEnabled(isShapeResizable(shapes))
      .rotateEnabled(isShapeRotatable(shapes))
      .visible(shapes.length > 0);

    // fire the even with setTimeout so that the stateChange has been processed with the
    // new position of the shape
    setTimeout(() => {
      stage.fire("close-voting", evt);
      stage.fire("selectionChange", evt);
      fireStateEvent("selectionChange", evt, stage, { selectedShapes: shapes.map((s) => s.state) });
      layer.batchDraw();
    });
  };

  const isShapeResizable = (shapes: WhiteboardShape[]) => {
    return shapes.length === 1 && shapes[0].selectionOptions.resizable === true;
  };

  const isShapeRotatable = (shapes: WhiteboardShape[]) => {
    return shapes.length === 1 && shapes[0].selectionOptions.rotatable === true;
  };

  //
  // multi selection
  //
  const multiSelectionLayer = new Layer({ listening: !context.readonly });
  stage.add(multiSelectionLayer);

  context.events.onLockWhiteboard(() => {
    multiSelectionLayer.destroy();
    stage.batchDraw();
  });

  // the rect we draw while selecting
  const multiSelectionRect = new Rect({
    name: "selection",
    fill: "rgba(0, 161, 255, 0.3)",
    listening: false,
    visible: false,
  });
  multiSelectionLayer.add(multiSelectionRect);

  const updateMultiSelectionRect = (p0: IPosition, p1: IPosition) => {
    multiSelectionRect.setAttrs({
      x: Math.min(p0.x, p1.x),
      y: Math.min(p0.y, p1.y),
      width: Math.abs(p0.x - p1.x),
      height: Math.abs(p0.y - p1.y),
      fill: "rgba(0, 161, 255, 0.3)",
      visible: true,
    });
  };

  // Detecting when we leave wb stage and we are in multi selection state
  // we attach a window event listener to have the ability to fire manually stage event to not broke the current wb action
  stage.on("mouseout", (evt) => {
    if (evt.target.children.length && isMultiSelecting) {
      attachMouseUpListener();
    }
  });

  stage.on("mousedown", (evt) => {
    if (!stage.isListening() || evt.target.attrs.name?.includes("voting")) return;

    mouseUpOnStageIsFired = false;

    initialPointerPos = relativePointerPosition(stage);

    if (evt.target !== stage) {
      const shape = evt.target.findAncestor(".shape", true) as WhiteboardShape;
      if (shape) {
        clickedShape = shape;
      }
      return;
    }

    isMultiSelecting = true;
    updateMultiSelectionRect(initialPointerPos, initialPointerPos);
  });

  // Detecting on mouse move event in wb stage and when we are in multiselection mode
  // propagate event up to the whiteboard-render class
  // which should be handled by onStartAutoScrolling method and produce auto scroll
  stage.on("mousemove", (evt) => {
    if (isMultiSelecting) {
      fireStateEvent("startAutoScrolling", evt, stage, {});
    }

    isTransforming = selectionTr.isTransforming();

    if (!stage.isListening() || !isMultiSelecting) {
      return;
    }

    updateMultiSelectionRect(initialPointerPos, relativePointerPosition(stage));
    multiSelectionLayer.batchDraw();
  });

  stage.on("mouseup", (evt) => {
    mouseUpOnStageIsFired = true;

    if (!stage.isListening() || isTransforming) {
      return;
    }
    // Detecting on mouse up event in wb stage and propagate event up to the whiteboard-render class
    // which should be handled by onStopAutoScrolling method and stop auto scroll
    fireStateEvent("stopAutoScrolling", evt, stage, {});

    if (evt.target.attrs.name === "voting-action voting") {
      let current = evt.target.parent;
      while (!current["state"]) {
        current = current.parent;
      }
      current.zIndex(guaranteedHighestIndex);
      stage.fire("close-voting", { shape: current });
      return;
    }

    const selectedShapes: WhiteboardShape[] = [];

    if (isMultiSelecting) {
      const selectionBox = multiSelectionRect.getClientRect();
      layer.find<WhiteboardShape>(".shape").each((shape) => {
        const shapeBox = shape.getClientRect();
        if (containsRect(selectionBox, shapeBox)) {
          selectedShapes.push(shape);
        }
      });

      if (selectedShapes.length > 1) {
        setSelectedShapes(selectedShapes, evt);
      } else if (selectedShapes.length === 1) {
        clickedShape = selectedShapes[0];
      }
    }

    if (clickedShape) {
      if (clickedShape.state.type === "note") {
        selection.shapes = selection.shapes.filter((s) => s.state.id !== clickedShape.state.id);
        setSelectedShapes([clickedShape], evt);
      }

      if (selectedSingleShape() !== clickedShape) {
        setSelectedShapes([clickedShape], evt);
      }
    } else if (!selectedShapes.length) {
      selectionTr.visible() ? setSelectedShapes([], evt) : stage.fire("close-voting", evt);
    }

    if (multiSelectionRect.visible()) {
      multiSelectionRect.hide();
      multiSelectionLayer.batchDraw();
    }

    stage.fire("close-voting", evt);

    clickedShape = undefined;
    initialPointerPos = undefined;
    isMultiSelecting = undefined;
    isTransforming = undefined;
  });

  renderer.selectedShapes = (shapes?: WhiteboardShape[]): WhiteboardShape[] => {
    if (typeof shapes === "undefined") {
      return selection.shapes;
    }

    setSelectedShapes(shapes);
  };

  const lockedShapesGroupsIdMap: Record<string, { lockedTransparentRect: Rect; userFirstName: Text; lockedSelectionTr: Transformer }> = {};

  const adjustLockIfLocked = (shape: WhiteboardShape): void => {
    const shapeGroup = lockedShapesGroupsIdMap[shape.id()];
    if (shapeGroup) {
      shapeGroup.lockedTransparentRect.position(shape.getPosition()).scale(shape.scale()).size(shapeSize(shape)).rotation(shape.rotation()).visible(true);
      shapeGroup.userFirstName.position(calculateTextCoordinates(shape));
    }
  };

  const lockShape = (shape: WhiteboardShape, config: { color: string; userFirstName: string }): void => {
    if (lockedShapesGroupsIdMap[shape.id()]) {
      return;
    }

    shape.listening(false);

    const lockedTransparentRect = new Rect({
      visible: false,
      rotation: shape.rotation(),
    });

    layer.add(lockedTransparentRect);

    const lockedSelectionTr = new Transformer({
      nodes: [lockedTransparentRect],
      padding: 10,
      borderStroke: config.color,
      borderStrokeWidth: 3,
      anchorCornerRadius: 50,
      anchorSize: 7,
      anchorStroke: "#96c6ff",
      rotateEnabled: false,
      keepRatio: false,
      ignoreStroke: true,
      resizeEnabled: false,
    });

    layer.add(lockedSelectionTr);

    const userFirstName = new Text({
      text: config.userFirstName,
      fontSize: 12,
      lineHeight: 1.2,
      padding: 10,
      fill: "#003851",
    });

    lockedShapesGroupsIdMap[shape.id()] = {
      lockedTransparentRect,
      lockedSelectionTr,
      userFirstName,
    };

    adjustLockIfLocked(shape);
    shape.fire(SHAPE_SELECTED);

    layer.add(userFirstName);
    layer.batchDraw();
  };

  const unlockShape = (...shapes: WhiteboardShape[]): void => {
    for (const shape of shapes) {
      if (!lockedShapesGroupsIdMap[shape.id()]) {
        continue;
      }

      shape.listening(true);
      Object.values(lockedShapesGroupsIdMap[shape.id()]).forEach((shape) => shape.destroy());

      delete lockedShapesGroupsIdMap[shape.id()];
      shape.fire(SHAPE_UNSELECTED);
    }

    layer.batchDraw();
  };

  const isShapeLocked = (shapeId: string): boolean => !!lockedShapesGroupsIdMap[shapeId];

  const calculateTextCoordinates = (shape: WhiteboardShape): IPosition => {
    return {
      x: shape.getPosition().x - 20,
      y: shape.getPosition().y - 40,
    };
  };

  const attachMouseUpListener = () => {
    window.addEventListener(
      "mouseup",
      () => {
        // Handle mouseup event on window object
        // but be sure that will fire stage mouse up event when is needed
        if (!mouseUpOnStageIsFired) {
          stage.fire("mouseup");
          mouseUpOnStageIsFired = true;
        }

        window.removeEventListener("mouseup", attachMouseUpListener, false);
      },
      { once: true }
    );
  };

  renderer.unlockShape = unlockShape;
  renderer.lockShape = lockShape;
  renderer.adjustLockIfLocked = adjustLockIfLocked;
  renderer.isShapeLocked = isShapeLocked;
};
